import { useSocketsLazyEvents } from "@/composables/ably/use-sockets-lazy-events";
import { CHANNELS } from "@/composables/ably/ably-channel-registry";
import { useUserStore } from "@/stores/user.store";
import { computed, inject, provide, type InjectionKey } from "vue";
import { useLoyaltyNotifications } from "./use-loyalty-notifications";
import { useTrackingStore } from "@/stores/tracking.store";
import type { LoyaltyPointsUpdateEvent } from "@f3/shared";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useChannelStore } from "@/stores/channel.store";
import { useShowStore } from "@/stores/show.store";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";

export const apiUserEventsInjectionKey = Symbol(
  "APIUserEvents",
) as InjectionKey<ReturnType<typeof useSetupApiUserEvents>>;

const apiUserEventsLogPrefix = "[APIUserEvents] ";
export function useSetupApiUserEvents() {
  const userStore = useUserStore();
  const _channelStore = useChannelStore();
  const showStore = useShowStore();
  const trackingService = useTrackingStore();
  const loyaltyNotifications = useLoyaltyNotifications();

  const { ready: readyToReceiveEvents } = useSocketsLazyEvents<
    LoyaltyPointsUpdateEvent,
    typeof CHANNELS.userEvents
  >({
    descriptor: CHANNELS.userEvents,
    channelArgs: () => [userStore.userId ?? ""] as [string],
    // Disable channel connection if the user is not logged in
    enabled: computed(() => !!userStore.userId),
    listener: (event) => {
      if (event && "points" in event) {
        const {
          points,
          message,
          experienceId,
          experienceType,
          hasMultiplierApplied,
        } = event;

        console.log(apiUserEventsLogPrefix + "Received points", points);
        loyaltyNotifications.addNotification({
          points,
          message: message ?? "",
          hasMultiplierApplied,
        });
        trackingService.track({
          event: TrackEventEnum.FanScoreUpdated,
          category: EventCategory.Engagement,
          metadata: {
            pointsScored: points,
            experienceId,
            experienceType,
          },
        });

        if (experienceType === "JOIN_SHOW") {
          setTimeout(() => {
            trackingService.track({
              event: TrackEventEnum.ShowViewRegistered,
              category: EventCategory.Engagement,
              metadata: {
                experienceId: experienceId ?? showStore.show?.id,
                experienceType: ExperienceType.Show,
                experienceAction: ExperienceAction.PREPARE_SHOW,
                experienceTitle: showStore.show?.title,
                experienceCreatorId: showStore.show?.created_by?.id,
                experienceCreatorUsername: showStore.show?.created_by?.username,
                experienceCommunityIds: showStore.show?.show_communities?.map(
                  (sc) => sc.community.id,
                ),
                experienceCommunityNames: showStore.show?.show_communities?.map(
                  (sc) => sc.community.name,
                ),
                // experienceMembership: channelStore.channelMemberships.find(
                //   (cm) =>
                //     cm.authorization_rule_id ===
                //     showStore.show?.authorization_rule_id,
                // )?.name,
              },
            });
          }, 1000);
        }
      } else {
        console.warn(
          apiUserEventsLogPrefix +
            "Received an unexpected loyalty event, ignoring",
          event,
        );
      }
    },
  });

  // TODO: Implement a queue that just stores the last event and processes it when ready
  const definition = { readyToReceiveEvents };
  provide(apiUserEventsInjectionKey, definition);
  return definition;
}

export function useApiUserEvents() {
  const apiUserEvents = inject(apiUserEventsInjectionKey);
  if (!apiUserEvents) {
    throw new Error(
      "useApiUserEvents() was called without a parent (or self) calling useSetupApiUserEvents() first",
    );
  }
  return apiUserEvents;
}
